<template>
    <authenticated-layout>
        <v-card class="pa-6">
            <v-card-title
                class="d-flex justify-space-between align-center pa-0 pb-2"
            >
                <v-btn
                    variant="flat"
                    size="small"
                    color="primary"
                    @click="onAddNew()"
                >
                    {{ $t("partners.Add new one") }}
                </v-btn>
            </v-card-title>

            <div class="overflow-x-auto">
                <table>
                    <tr>
                        <th
                            v-for="(header, index) in headers"
                            :key="`table-header-${index}`"
                            class="text-left whitespace-nowrap"
                        >
                            {{ header }}
                        </th>
                    </tr>

                    <tr v-for="row in data" :key="`partner-${row.id}`">
                        <td>{{ row.id }}</td>
                        <td>{{ row.name }}</td>
                        <td class="truncate max-w-xs">
                            {{ row.notes ?? "-" }}
                        </td>
                        <td>{{ row.url ?? "-" }}</td>
                        <td>
                            <img
                                v-if="row.image_url"
                                :src="row.image_url"
                                alt="partner logo"
                                class="w-12"
                            />

                            <span v-else>-</span>
                        </td>
                        <td class="text-right">
                            <v-btn
                                variant="flat"
                                size="x-small"
                                color="primary"
                                @click="editRow(row)"
                            >
                                {{ $t("partners.Edit") }}
                            </v-btn>

                            <v-btn
                                variant="flat"
                                size="x-small"
                                class="ml-2"
                                color="error"
                                @click="deleteRow(row)"
                            >
                                {{ $t("partners.Delete") }}
                            </v-btn>
                        </td>
                    </tr>
                </table>
            </div>

            <v-dialog v-model="showModal" max-width="700" persistent scrollable>
                <v-card>
                    <v-card-title class="text-subtitle-1">
                        {{
                            editMode
                                ? $t("partners.Update")
                                : $t("partners.Add new one")
                        }}
                    </v-card-title>

                    <v-divider />

                    <v-card-text style="max-height: 600px">
                        <v-text-field
                            v-model="form.name"
                            :label="$t('partners.Name')"
                            variant="outlined"
                            density="compact"
                            type="input"
                            :error-messages="
                                errors.name ? [errors.name[0]] : []
                            "
                        />

                        <v-textarea
                            v-model="form.notes"
                            :label="$t('partners.Notes')"
                            variant="outlined"
                            density="compact"
                            :error-messages="
                                errors.notes ? [errors.notes[0]] : []
                            "
                        />

                        <v-text-field
                            v-model="form.url"
                            :label="$t('partners.URL')"
                            variant="outlined"
                            density="compact"
                            type="input"
                            :error-messages="errors.url ? [errors.url[0]] : []"
                        />

                        <div class="mb-4">
                            <label
                                for="logo"
                                class="block text-gray-700 text-sm font-bold mb-2"
                            >
                                {{ $t("partners.Logo") }}
                            </label>

                            <div v-if="editMode">
                                <div
                                    v-show="!uploadPartnerLogo"
                                    class="flex justify-start items-center"
                                >
                                    <img
                                        v-if="form.image_url"
                                        :src="form.image_url"
                                        width="70"
                                    />

                                    <div>
                                        <button
                                            @click="uploadPartnerLogo = true"
                                            type="button"
                                            class="bg-blue-500 hover:bg-blue-700 text-white font-bold ml-3 py-1 px-2 text-sm rounded"
                                        >
                                            {{ $t("partners.Upload new logo") }}
                                        </button>
                                    </div>
                                </div>

                                <input
                                    v-show="uploadPartnerLogo"
                                    type="file"
                                    id="logo"
                                    class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                    ref="logo"
                                    accept="image/*"
                                />
                            </div>

                            <div v-else>
                                <input
                                    type="file"
                                    id="logo"
                                    class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                    ref="logo"
                                    accept="image/*"
                                />
                            </div>

                            <div v-if="errors.logo" class="text-red-500">
                                {{ errors.logo[0] }}
                            </div>
                        </div>
                    </v-card-text>

                    <v-divider />

                    <v-card-actions>
                        <v-spacer />

                        <v-btn variant="flat" size="small" @click="cancel()">
                            {{ $t("partners.Cancel") }}
                        </v-btn>

                        <v-btn
                            v-show="!editMode"
                            color="secondary"
                            variant="flat"
                            size="small"
                            @click="onSubmit()"
                        >
                            {{ $t("partners.Save") }}
                        </v-btn>

                        <v-btn
                            v-show="editMode"
                            color="secondary"
                            variant="flat"
                            size="small"
                            @click="onSubmit()"
                        >
                            {{ $t("partners.Update") }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-card>
    </authenticated-layout>
</template>

<script>
import AuthenticatedLayout from "@/Layouts/Authenticated";

export default {
    props: ["data", "errors"],
    components: {
        AuthenticatedLayout,
    },
    data() {
        return {
            headers: [
                "ID",
                this.$t("partners.Name"),
                this.$t("partners.Notes"),
                this.$t("partners.URL"),
                this.$t("partners.Logo"),
                "",
            ],
            showModal: false,
            editMode: false,
            form: {
                id: null,
                name: null,
                notes: null,
                url: null,
                image_url: null,
            },
            uploadPartnerLogo: false,
        };
    },
    methods: {
        onAddNew() {
            this.showModal = true;
        },
        editRow(row) {
            this.form = Object.assign({}, row);

            this.editMode = true;
            this.showModal = true;
        },
        cancel() {
            this.showModal = false;
            this.editMode = false;
            this.uploadPartnerLogo = false;

            this.formReset();
        },
        deleteRow({ id }) {
            if (!confirm(this.$t("partners.delete-confirmation-message"))) {
                return;
            }

            this.$inertia.delete(this.route("partners-panel.delete", id));
        },
        onSubmit() {
            let data = this.form;

            data.logo = this.$refs.logo.files[0] || null;

            if (this.editMode) {
                this.$inertia.post(this.route("partners-panel.update"), data);
            } else {
                this.$inertia.post(this.route("partners-panel.store"), data);
            }

            this.cancel();
        },
        formReset() {
            this.form = {
                id: null,
                name: null,
                notes: null,
                url: null,
                image_url: null,
            };
        },
    },
};
</script>

<style scoped>
table {
    width: 100%;
    table-layout: fixed;
    font-size: 12px;
}

@media screen and (max-width: 1024px) {
    table {
        width: auto;
    }
}

th {
    padding: 10px 0px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    text-align: left;
    box-sizing: border-box;
    padding-left: 10px;
    border-bottom: 1px solid #aaa;
}

td {
    text-align: left;
    box-sizing: border-box;
    padding: 5px 0px;
    padding-left: 10px;
    border-bottom: 1px solid #ccc;
}
</style>
